'use client'

import { useState } from 'react'
import { Button } from './button'
import { cn } from '@/lib/utils'

interface LanguageSwitcherProps {
  originalText?: string
  englishText?: string
  className?: string
  size?: 'sm' | 'md' | 'lg'
}

export function LanguageSwitcher({ 
  originalText, 
  englishText, 
  className,
  size = 'sm'
}: LanguageSwitcherProps) {
  const [selectedLanguage, setSelectedLanguage] = useState<'original' | 'english'>('original')

  // If both texts are the same or one is missing, show only one option
  const hasMultipleLanguages = originalText && englishText && originalText !== englishText

  if (!hasMultipleLanguages) {
    return (
      <div className={cn("text-sm text-gray-700 leading-relaxed text-justify", className)}>
        {originalText || englishText || 'No content available.'}
      </div>
    )
  }

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  }

  return (
    <div className={className}>
      {/* Language Toggle Buttons */}
      <div className="flex rounded-md border border-gray-200 bg-gray-50 p-1 mb-3 w-fit">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedLanguage('original')}
          className={cn(
            sizeClasses[size],
            "rounded transition-colors",
            selectedLanguage === 'original'
              ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
              : 'text-gray-600 hover:text-gray-900'
          )}
        >
          Original
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setSelectedLanguage('english')}
          className={cn(
            sizeClasses[size],
            "rounded transition-colors",
            selectedLanguage === 'english'
              ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
              : 'text-gray-600 hover:text-gray-900'
          )}
        >
          English
        </Button>
      </div>

      {/* Content Display */}
      <div className="text-sm text-gray-700 leading-relaxed text-justify">
        {selectedLanguage === 'original' ? originalText : englishText}
      </div>
    </div>
  )
}
